import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDateTime } from "../../utils/dateFormat";
import { 
  MessageSquare, Plus, Pencil, Trash2, X, Save, 
  Loader2, Languages, Globe, Copy, AlertTriangle
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { PageInfoInline } from "./PageInfo";

// Helper function to render text with *bold* syntax
function renderBoldText(text) {
  if (!text) return null;
  
  // Split by *word* pattern and render bold parts
  const parts = text.split(/(\*[^*]+\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      // Remove asterisks and render as bold
      return <strong key={index}>{part.slice(1, -1)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

// Confirmation Modal Component
function ConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Conferma",
  cancelText = "Annulla",
  confirmVariant = "danger", // "danger" or "primary"
  loading = false
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`p-2 rounded-full ${
              confirmVariant === "danger" ? "bg-red-100" : "bg-[#D4AF37]/10"
            }`}>
              <AlertTriangle size={24} className={
                confirmVariant === "danger" ? "text-red-600" : "text-[#D4AF37]"
              } />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-lg font-semibold text-[#1C1917]">
                {title}
              </h3>
              <p className="text-sm text-[#78716C] mt-2">
                {message}
              </p>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={loading}
            >
              {cancelText}
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={loading}
              className={
                confirmVariant === "danger" 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-[#D4AF37] hover:bg-[#B8972E] text-white"
              }
            >
              {loading ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> Elaborazione...</>
              ) : (
                confirmText
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Supported languages in order (matching backend)
const LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "pt", name: "Português", flag: "🇵🇹" },
  { code: "ru", name: "Русский", flag: "🇷🇺" },
  { code: "zh-Hans", name: "简体中文", flag: "🇨🇳" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ar", name: "العربية", flag: "🇸🇦" },
  { code: "hi", name: "हिन्दी", flag: "🇮🇳" },
];

// Message Card Component
function MessageCard({ message, onEdit, onDelete, onTranslate }) {
  const [showFullContent, setShowFullContent] = useState(false);
  
  const contentPreview = message.content?.length > 150 
    ? message.content.substring(0, 150) + "..." 
    : message.content;

  // Copy message content to clipboard (without asterisks for clean text)
  const handleCopyMessage = async () => {
    try {
      // Remove asterisks for clean copy, but keep the text
      const cleanContent = message.content?.replace(/\*([^*]+)\*/g, '$1') || '';
      await navigator.clipboard.writeText(cleanContent);
      toast.success("Messaggio copiato negli appunti");
    } catch (err) {
      toast.error("Errore nella copia del messaggio");
    }
  };

  return (
    <div className="border border-[#E7E5E4] rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Title - Bold and larger */}
          <h3 className="font-semibold text-lg text-[#1C1917]">{message.title}</h3>
          {message.description && (
            <p className="text-xs text-[#78716C] mt-1 italic">{message.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyMessage}
            title="Copia messaggio"
            className="text-[#78716C] hover:text-[#1C1917]"
          >
            <Copy size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onTranslate(message)}
            title="Genera traduzioni in altre lingue"
            className="text-[#78716C] hover:text-[#D4AF37]"
          >
            <Globe size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(message)}
            className="text-[#78716C] hover:text-[#1C1917]"
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(message)}
            className="text-[#78716C] hover:text-red-600"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
      
      {/* Content with inline bold support (*word*) */}
      <div className="mt-3 p-3 bg-[#F9F8F6] rounded-md">
        <p className="text-sm text-[#44403C] whitespace-pre-wrap">
          {renderBoldText(showFullContent ? message.content : contentPreview)}
        </p>
        {message.content?.length > 150 && (
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="text-xs text-[#D4AF37] mt-2 hover:underline"
          >
            {showFullContent ? "Mostra meno" : "Mostra tutto"}
          </button>
        )}
      </div>
      
      <div className="mt-2 text-xs text-[#A8A29E]">
        Aggiornato: {formatDisplayDateTime(message.updated_at)}
      </div>
    </div>
  );
}

// Message Form Modal
function MessageFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  message = null, 
  selectedLang,
  saving 
}) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: ""
  });

  useEffect(() => {
    if (message) {
      setFormData({
        title: message.title || "",
        description: message.description || "",
        content: message.content || ""
      });
    } else {
      setFormData({ title: "", description: "", content: "" });
    }
  }, [message, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Il contenuto è obbligatorio");
      return;
    }
    onSave(formData);
  };

  if (!isOpen) return null;

  const langInfo = LANGUAGES.find(l => l.code === selectedLang) || LANGUAGES[0];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E7E5E4]">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl flex items-center gap-2">
              <span className="text-2xl">{langInfo.flag}</span>
              {message ? "Modifica Messaggio" : "Nuovo Messaggio"}
              <span className="text-sm font-normal text-[#78716C]">({langInfo.name})</span>
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X size={20} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-[#44403C] mb-1 block">
              Titolo <span className="text-red-500">*</span>
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Inserisci il titolo del messaggio"
              required
            />
            <p className="text-xs text-[#A8A29E] mt-1">Utilizzato per l'ordinamento alfabetico</p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#44403C] mb-1 block">
              Descrizione <span className="text-[#A8A29E]">(opzionale)</span>
            </label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione interna per identificare il messaggio"
            />
            <p className="text-xs text-[#A8A29E] mt-1">A uso interno, non visibile pubblicamente</p>
          </div>

          <div>
            <label className="text-sm font-medium text-[#44403C] mb-1 block">
              Contenuto del messaggio <span className="text-red-500">*</span>
            </label>
            
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Inserisci il contenuto del messaggio..."
              rows={6}
              required
              className="resize-y"
            />
            <p className="text-xs text-[#78716C] mt-1">
              Usa un asterisco prima e dopo la parola per il grassetto. Esempio: <span className="font-mono bg-[#F5F5F4] px-1 rounded">*word*</span>
            </p>
            
            {/* Live Preview */}
            {formData.content && (
              <div className="mt-3">
                <p className="text-xs font-medium text-[#78716C] mb-1">Anteprima:</p>
                <div className="p-3 bg-[#F9F8F6] rounded-md border border-[#E7E5E4]">
                  <p className="text-sm text-[#44403C] whitespace-pre-wrap">
                    {renderBoldText(formData.content)}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E7E5E4]">
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
              disabled={saving}
            >
              {saving ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> Salvataggio...</>
              ) : (
                <><Save className="mr-2" size={16} /> Salva</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Translate Modal - Generate translations to other languages
function TranslateModal({ 
  isOpen, 
  onClose, 
  message, 
  onTranslate,
  translating 
}) {
  const [selectedLangs, setSelectedLangs] = useState([]);

  useEffect(() => {
    if (isOpen && message) {
      // Pre-select all languages except the source
      setSelectedLangs(LANGUAGES.filter(l => l.code !== message.lang).map(l => l.code));
    }
  }, [isOpen, message]);

  const toggleLang = (code) => {
    setSelectedLangs(prev => 
      prev.includes(code) 
        ? prev.filter(c => c !== code)
        : [...prev, code]
    );
  };

  const selectAll = () => {
    setSelectedLangs(LANGUAGES.filter(l => l.code !== message?.lang).map(l => l.code));
  };

  const deselectAll = () => {
    setSelectedLangs([]);
  };

  const handleTranslate = () => {
    if (selectedLangs.length === 0) {
      toast.error("Seleziona almeno una lingua");
      return;
    }
    onTranslate(selectedLangs);
  };

  if (!isOpen || !message) return null;

  const sourceLang = LANGUAGES.find(l => l.code === message.lang);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-[#E7E5E4]">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl flex items-center gap-2">
              <Globe size={24} className="text-[#D4AF37]" />
              Genera Traduzioni
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X size={20} />
            </Button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-3 bg-[#F9F8F6] rounded-lg">
            <p className="text-sm text-[#78716C]">Messaggio originale:</p>
            <p className="font-semibold text-lg mt-1">{message.title}</p>
            <p className="text-xs text-[#A8A29E] mt-1">
              Lingua: {sourceLang?.flag} {sourceLang?.name}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#44403C]">
                Seleziona le lingue di destinazione:
              </p>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="text-xs text-[#D4AF37] hover:underline"
                >
                  Seleziona tutte
                </button>
                <span className="text-xs text-[#E7E5E4]">|</span>
                <button
                  onClick={deselectAll}
                  className="text-xs text-[#78716C] hover:underline"
                >
                  Deseleziona
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {LANGUAGES.filter(l => l.code !== message.lang).map(lang => (
                <label
                  key={lang.code}
                  className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                    selectedLangs.includes(lang.code)
                      ? "border-[#D4AF37] bg-[#D4AF37]/10"
                      : "border-[#E7E5E4] hover:border-[#D4AF37]/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedLangs.includes(lang.code)}
                    onChange={() => toggleLang(lang.code)}
                    className="sr-only"
                  />
                  <span className="text-lg">{lang.flag}</span>
                  <span className="text-sm">{lang.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-700">
              <strong>Nota:</strong> I testi (titolo, descrizione e contenuto) verranno tradotti automaticamente tramite AI nelle lingue selezionate. 
              Potrai modificarli liberamente in seguito — ogni lingua resta indipendente.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#E7E5E4]">
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button 
              onClick={handleTranslate}
              className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
              disabled={translating || selectedLangs.length === 0}
            >
              {translating ? (
                <><Loader2 className="animate-spin mr-2" size={16} /> Generazione...</>
              ) : (
                <><Globe className="mr-2" size={16} /> Genera ({selectedLangs.length})</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Messages Page
export default function MessagesPage({ getAuthHeaders }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLang, setSelectedLang] = useState("en");
  
  // Modal states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Translate modal
  const [translateModalOpen, setTranslateModalOpen] = useState(false);
  const [translatingMessage, setTranslatingMessage] = useState(null);
  const [translating, setTranslating] = useState(false);

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load messages
  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/messages?lang=${selectedLang}`,
        getAuthHeaders()
      );
      setMessages(res.data.messages || []);
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Errore nel caricamento dei messaggi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [selectedLang]);

  // Create/Update message
  const handleSaveMessage = async (formData) => {
    setSaving(true);
    try {
      if (editingMessage) {
        // Update existing
        await axios.put(
          `${API}/admin/messages/${editingMessage.message_id}`,
          formData,
          getAuthHeaders()
        );
        toast.success("Messaggio aggiornato");
      } else {
        // Create new
        await axios.post(
          `${API}/admin/messages`,
          { ...formData, lang: selectedLang },
          getAuthHeaders()
        );
        toast.success("Messaggio creato");
      }
      
      setFormModalOpen(false);
      setEditingMessage(null);
      loadMessages();
    } catch (error) {
      console.error("Error saving message:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Open delete confirmation modal
  const handleDeleteMessage = (message) => {
    setDeletingMessage(message);
    setDeleteModalOpen(true);
  };

  // Confirm delete message
  const confirmDeleteMessage = async () => {
    if (!deletingMessage) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/messages/${deletingMessage.message_id}`,
        getAuthHeaders()
      );
      toast.success("Messaggio eliminato con successo");
      setDeleteModalOpen(false);
      setDeletingMessage(null);
      loadMessages();
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Errore nell'eliminazione del messaggio");
    } finally {
      setDeleting(false);
    }
  };

  // Open edit modal
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setFormModalOpen(true);
  };

  // Open translate modal
  const handleOpenTranslate = (message) => {
    setTranslatingMessage(message);
    setTranslateModalOpen(true);
  };

  // Execute translation - generates copies in other languages
  const handleTranslate = async (targetLangs) => {
    setTranslating(true);
    try {
      const params = new URLSearchParams();
      targetLangs.forEach(lang => params.append("target_langs", lang));
      
      const res = await axios.post(
        `${API}/admin/messages/${translatingMessage.message_id}/translate?${params.toString()}`,
        {},
        getAuthHeaders()
      );
      
      const translations = res.data.translations || [];
      const created = translations.filter(t => t.action === "created").length;
      const updated = translations.filter(t => t.action === "updated").length;
      
      let successMsg = `Traduzioni generate: ${created} nuove`;
      if (updated > 0) {
        successMsg += `, ${updated} aggiornate`;
      }
      toast.success(successMsg);
      
      setTranslateModalOpen(false);
      setTranslatingMessage(null);
    } catch (error) {
      console.error("Error translating:", error);
      toast.error("Errore nella generazione delle traduzioni");
    } finally {
      setTranslating(false);
    }
  };

  const currentLangInfo = LANGUAGES.find(l => l.code === selectedLang) || LANGUAGES[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Messages</h1>
        <PageInfoInline pageKey="messages" />
      </div>

      {/* Language Selector */}
      <div className="admin-card">
        <h2 className="font-heading text-lg mb-4 flex items-center gap-2">
          <Languages size={20} className="text-[#D4AF37]" />
          Seleziona Lingua
        </h2>
        
        <div className="flex flex-wrap gap-2">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => setSelectedLang(lang.code)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                selectedLang === lang.code
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#1C1917] font-medium"
                  : "border-[#E7E5E4] hover:border-[#D4AF37]/50 text-[#78716C]"
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm">{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Messages List */}
      <div className="admin-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg flex items-center gap-2">
            <span className="text-2xl">{currentLangInfo.flag}</span>
            Messages in {currentLangInfo.name}
            <span className="text-sm font-normal text-[#78716C]">
              ({messages.length} {messages.length === 1 ? "message" : "messages"})
            </span>
          </h2>
          
          <Button
            onClick={() => {
              setEditingMessage(null);
              setFormModalOpen(true);
            }}
            className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
          >
            <Plus size={16} className="mr-2" />
            Add message
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-[#78716C]">
            <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
            <p>No messages in {currentLangInfo.name}</p>
            <p className="text-sm mt-1">Click "Add message" to create one</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {messages.map(message => (
              <MessageCard
                key={message.message_id}
                message={message}
                onEdit={handleEditMessage}
                onDelete={handleDeleteMessage}
                onTranslate={handleOpenTranslate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      <MessageFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingMessage(null);
        }}
        onSave={handleSaveMessage}
        message={editingMessage}
        selectedLang={selectedLang}
        saving={saving}
      />

      {/* Translate Modal */}
      <TranslateModal
        isOpen={translateModalOpen}
        onClose={() => {
          setTranslateModalOpen(false);
          setTranslatingMessage(null);
        }}
        message={translatingMessage}
        onTranslate={handleTranslate}
        translating={translating}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingMessage(null);
        }}
        onConfirm={confirmDeleteMessage}
        title="Elimina messaggio"
        message={`Sei sicuro di voler eliminare il messaggio "${deletingMessage?.title}"? Questa azione è irreversibile.`}
        confirmText="Elimina"
        cancelText="Annulla"
        confirmVariant="danger"
        loading={deleting}
      />
    </div>
  );
}
